/**
 * "Къде са парите" — the other axis, across all funds.
 *
 * Cash held by somebody else (the entrance's cashier) is called out
 * separately: it is money you are answerable for but cannot reach, and that
 * distinction matters far more day to day than bank-versus-cash.
 */
import { Banknote, CreditCard, Landmark, UserRound, Wallet } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MultiCurrency } from '@/components/Money';
import { formatMoney, formatPercent } from '@/lib/format';
import type { LocationBalance, LocationKind } from '@/lib/types';

const ICONS: Record<LocationKind, React.ComponentType<{ className?: string }>> = {
  BANK: Landmark,
  CASH: Banknote,
  CARD: CreditCard,
  OTHER: Wallet,
};

export function MoneyMap({
  locations,
  baseCurrency,
  baseDecimals,
  totalMinor,
}: {
  locations: LocationBalance[];
  baseCurrency: string;
  baseDecimals: number;
  totalMinor: number;
}): JSX.Element | null {
  const meaningful = locations.filter((l) => l.baseMinor !== 0);
  if (meaningful.length === 0) return null;

  const heldByOthers = meaningful.filter((l) => l.holderName);
  const heldByOthersTotal = heldByOthers.reduce((n, l) => n + l.baseMinor, 0);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Къде са парите</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Proportion bar — the shape of the split at a glance. */}
        <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
          {meaningful.map((loc, i) => {
            const share = totalMinor > 0 ? loc.baseMinor / totalMinor : 0;
            if (share <= 0) return null;
            return (
              <div
                key={loc.locationId}
                className={cn(
                  'h-full',
                  ['bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500'][
                    i % 5
                  ],
                )}
                style={{ width: `${share * 100}%` }}
              />
            );
          })}
        </div>

        <div className="space-y-2">
          {meaningful.map((loc, i) => {
            const Icon = ICONS[loc.locationKind] ?? Wallet;
            const share = totalMinor > 0 ? loc.baseMinor / totalMinor : 0;
            return (
              <div key={loc.locationId} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      'h-2 w-2 shrink-0 rounded-full',
                      ['bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500'][
                        i % 5
                      ],
                    )}
                  />
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {loc.locationName}
                    {loc.holderName ? (
                      <span className="text-muted-foreground"> · {loc.holderName}</span>
                    ) : null}
                  </span>
                  <span className="tabular shrink-0 text-xs text-muted-foreground">
                    {formatPercent(share)}
                  </span>
                </span>
                <MultiCurrency
                  totals={loc.byCurrency}
                  baseMinor={loc.baseMinor}
                  baseCurrency={baseCurrency}
                  baseDecimals={baseDecimals}
                  className="shrink-0"
                  compact
                />
              </div>
            );
          })}
        </div>

        {heldByOthers.length > 0 ? (
          <p className="flex items-center gap-2 rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
            <UserRound className="h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="tabular font-medium text-foreground">
                {formatMoney(heldByOthersTotal, baseCurrency, baseDecimals)}
              </span>{' '}
              се държат от друг човек — не са у теб.
            </span>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
